import React, { useEffect, useState, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Line } from 'react-chartjs-2';
import * as Actions from 'redux/actions/index';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import UpdateUserLevelModal from 'routes/Admin/Dashboard/UpdateUserLevelModal';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';

import './UserLevelGraph.scss';
import { isAdmin } from 'services/adminCheck';
import moment from 'moment';

const getOrCreateTooltip = (chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.style.background = '#FFFFFF';
    tooltipEl.style.borderRadius = '3px';
    tooltipEl.style.color = 'black';
    tooltipEl.style.opacity = 1;
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.position = 'absolute';
    // tooltipEl.style.transform = 'translate(-50%, 0)';
    tooltipEl.style.transition = 'all .1s ease';
    tooltipEl.style.boxShadow = '0 5px 15px rgba(0, 0, 0, 0.5)';

    const table = document.createElement('table');
    table.style.margin = '0px';
    table.style.minWidth = '400px';

    tooltipEl.appendChild(table);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const createHeaderRow = (label, value, bottomPadding = 0) => {
  const tr = document.createElement('tr');
  tr.style.borderWidth = 0;

  const th1 = document.createElement('th');
  th1.style.borderWidth = 0;
  th1.style.width = '200px';
  th1.style.textAlign = 'left';
  th1.style.paddingBottom = `${bottomPadding}px`;

  const labelText = document.createTextNode(`${label}: `);
  const labelContainer = document.createElement('b');
  labelContainer.style.width = '45px';
  labelContainer.style.display = 'inline-block';
  labelContainer.appendChild(labelText);

  const valueText = document.createTextNode(value);
  const valueContainer = document.createElement('span');
  valueContainer.style.fontWeight = '400';
  valueContainer.appendChild(valueText);

  th1.appendChild(labelContainer);
  th1.appendChild(valueContainer);

  tr.appendChild(th1);

  return tr;
};

const createTableHeaderRow = () => {
  const tr = document.createElement('tr');
  tr.style.borderWidth = 0;

  const th1 = document.createElement('th');
  th1.style.borderWidth = 0;
  th1.style.width = '200px';
  th1.style.textAlign = 'left';
  const text1 = document.createTextNode('Goal');
  th1.appendChild(text1);

  const th2 = document.createElement('th');
  th2.style.borderWidth = 0;
  th2.style.width = '100px';
  th2.style.textAlign = 'center';
  const text2 = document.createTextNode('Completed');
  th2.appendChild(text2);

  const th3 = document.createElement('th');
  th3.style.borderWidth = 0;
  th3.style.width = '30px';
  th3.style.textAlign = 'center';
  const text3 = document.createTextNode('Status');
  th3.appendChild(text3);

  tr.appendChild(th1);
  tr.appendChild(th2);
  tr.appendChild(th3);

  return tr;
};

const renderStatusCheck = (status) => {
  const div = document.createElement('div');
  div.style.margin = 'auto';
  div.style.width = '16px';
  div.style.height = '16px';
  div.style.borderRadius = '16px';
  div.style.backgroundColor = status ? '#7fb800' : '#979797';

  if (status) {
    const image = document.createElement('div');
    image.style.width = '16px';
    image.style.height = '16px';
    image.style.background =
      "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAYAAABWdVznAAAACXBIWXMAAA7EAAAOxAGVKw4bAAAAjElEQVQoz7WRKw7CQBBAJ5OepxKNQiGqEKgKBApRUdEbVFUiKkgPiEIQQmZfBdt0uyGwAp6ceZmvyD8A9sDFzDRLkHciMohIpqpf5QJ48sKAMmyZR/IWeATyYUqUPnAFVj62Ae7MHMNKfZC4AXUknxZzmpkCZ95TfVqwi+Q65d6tl5vkJznn1j/49cwIO2O7/yMt/UIAAAAASUVORK5CYII=')";
    image.style.backgroundRepeat = 'no-repeat';
    image.style.backgroundSize = '12px 12px';
    image.style.backgroundPosition = 'center';

    div.appendChild(image);
  }

  return div;
};

const createRow = (goalText, completed, status) => {
  const tr = document.createElement('tr');
  tr.style.borderWidth = 0;

  const td1 = document.createElement('td');
  td1.style.borderWidth = 0;
  td1.style.paddingTop = '2px';
  td1.style.paddingBottom = '2px';
  td1.style.width = '200px';
  td1.style.textAlign = 'left';
  td1.style.textWrap = 'nowrap';
  const text1 = document.createTextNode(goalText);
  td1.appendChild(text1);

  const td2 = document.createElement('td');
  td2.style.borderWidth = 0;
  td2.style.paddingTop = '2px';
  td2.style.paddingBottom = '2px';
  td2.style.width = '100px';
  td2.style.textAlign = 'center';
  const text2 = document.createTextNode(completed);
  td2.appendChild(text2);

  const td3 = document.createElement('td');
  td3.style.borderWidth = 0;
  td3.style.paddingTop = '2px';
  td3.style.paddingBottom = '2px';
  td3.style.width = '30px';
  td3.style.textAlign = 'center';
  const text3 = renderStatusCheck(status);
  td3.appendChild(text3);

  tr.appendChild(td1);
  tr.appendChild(td2);
  tr.appendChild(td3);

  return tr;
};

const externalTooltipHandler = (context) => {
  // Tooltip Element
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  if (!Array.isArray(tooltip.dataPoints) || tooltip.dataPoints.length === 0) {
    return;
  }

  const dataIndex = tooltip.dataPoints[0].dataIndex;
  const level = tooltip.dataPoints[0].dataset.data[dataIndex];
  const nextLevel = tooltip.dataPoints[0].dataset.nextLevel[dataIndex];
  const progress = tooltip.dataPoints[0].dataset.progress[dataIndex];
  const startWeek = tooltip.dataPoints[0].dataset.startWeek[dataIndex];
  const endWeek = tooltip.dataPoints[0].dataset.endWeek[dataIndex];

  // Set Text
  if (tooltip.body) {
    const tableHead = document.createElement('thead');

    const tr1 = createHeaderRow(
      'Start',
      `Level ${level} on ${moment(startWeek).format('MMMM D, YYYY')}`,
      nextLevel ? 0 : 8,
    );
    const tr2 = createHeaderRow(
      'End',
      `Level ${nextLevel} on ${moment(endWeek)
        .add(1, 'day')
        .format('MMMM D, YYYY')}`,
      8,
    );

    tableHead.appendChild(tr1);
    if (nextLevel) {
      tableHead.appendChild(tr2);
    }

    // const div = document.createElement('div');
    // div.style.width = '100%';
    // div.style.height = '8px';

    // tableHead.appendChild(div);

    const tableBody = document.createElement('tbody');

    const tr5 = createTableHeaderRow();
    tableBody.appendChild(tr5);

    for (const progressGoal in progress) {
      const progressGoalObj = progress[progressGoal];

      const tr = createRow(
        progressGoalObj.goalText,
        progressGoalObj.completed,
        progressGoalObj.status,
      );

      tableBody.appendChild(tr);
    }

    const tableRoot = tooltipEl.querySelector('table');

    // Remove old children
    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    // Add new children
    tableRoot.appendChild(tableHead);
    tableRoot.appendChild(tableBody);
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1;
  let left = positionX + tooltip.caretX;

  if (left + tooltipEl.offsetWidth > document.body.offsetWidth) {
    left = document.body.offsetWidth - tooltipEl.offsetWidth - 40;
  }

  tooltipEl.style.left = left + 'px';
  tooltipEl.style.top = positionY + tooltip.caretY + 'px';
  tooltipEl.style.font = tooltip.options.bodyFont.string;
  tooltipEl.style.padding =
    tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';
};

const options = () => ({
  responsive: true,
  maintainAspectRatio: false,
  elements: {
    line: {
      tension: 0,
    },
  },
  scales: {
    y: {
      suggestedMin: 1,
      min: 1,
      max: 6,
      ticks: {
        precision: 0,
        stepSize: 1,
        font: {
          size: 12,
        },
      },
      grid: {
        display: false,
      },
    },
    x: {
      grid: {
        display: false,
      },
      ticks: {
        font: {
          size: 12,
        },
      },
      ticks: {
        callback: function (value, index, values) {
          const label = this.getLabelForValue(value); // Get actual label value
          const currentDate = moment(label, 'YYYY-MM-DD');
          const prevLabel =
            index > 0 ? this.getLabelForValue(values[index - 1].value) : null;
          const prevDate = prevLabel ? moment(prevLabel, 'YYYY-MM-DD') : null;

          // Show year if it's the first label or if the year changes
          if (!prevDate || currentDate.year() !== prevDate.year()) {
            return currentDate.format('YYYY-MM-DD'); // Example: "2023 Jan 01"
          }

          return currentDate.format('MM-DD'); // Example: "Jan 01"
        },
      },
    },
  },
  legend: {
    display: true,
    labels: {
      padding: 20,
    },
  },
  plugins: {
    beforeInit: function (chart) {
      // Get reference to the original fit function
      const originalFit = chart.legend.fit;

      // Override the fit function
      chart.legend.fit = function fit() {
        // Call original function and bind scope in order to use `this` correctly inside it
        originalFit.bind(chart.legend)();
        // Change the height as suggested in another answers
        this.height += 15;
      };
    },
    datalabels: {
      font: {
        weight: 'bold',
      },
      display: true,
      color: '#000000',
      offset: 2,
      align: 'top',
    },
    tooltip: {
      enabled: false,
      // position: 'nearest',
      external: externalTooltipHandler,
    },
  },
});

const plugin = {
  id: 'increase-legend-spacing',
  beforeInit(chart) {
    // Get reference to the original fit function
    const originalFit = chart.legend.fit;

    // Override the fit function
    chart.legend.fit = function fit() {
      // Call original function and bind scope in order to use `this` correctly inside it
      originalFit.bind(chart.legend)();
      // Change the height as suggested in another answers
      this.height += 30;
    };
  },
};

function UserLevelGraph({ username, actions }) {
  const [userProgress, setUserProgress] = useState([]);
  const [userLevel, setUserLevel] = useState(null);
  const [showUpdateUserLevelModal, setShowUpdateUserLevelModal] =
    useState(false);
  const chartRef = useRef(null);

  const refetch = () => {
    actions
      .getUserLevelHistory(username)
      .then((response) => {
        const data = response.data.response;
        const level = response.data.level;

        setUserProgress(data);
        setUserLevel(level);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!username || username === 'all') {
      return;
    }

    refetch();
  }, [username]);

  const chartData = useMemo(() => {
    return {
      labels: userProgress.map((a) => a.forWeek),
      datasets: [
        {
          label: `User level history - ${username}`,
          data: userProgress.map((a) => a.level),
          borderColor: '#1286c8',
          backgroundColor: 'rgba(210, 230, 245, 0.3)',
          progress: userProgress.map((a) => a.progress),
          nextLevel: userProgress.map((a) => a.nextLevel),
          startWeek: userProgress.map((a) => a.startWeek),
          endWeek: userProgress.map((a) => a.endWeek),
        },
      ],
    };
  }, [userProgress]);

  // useEffect(() => {
  //   console.log('Updating...', chartRef.current);
  //   if (chartRef.current && chartRef.current.chartInstance) {
  //     chartRef.current.chartInstance.update()
  //   }
  // }, [chartData]);

  if (!username || username === 'all') {
    return (
      <div className="user-level-graph no-user av-gray">
        Select user to see their level history
      </div>
    );
  }

  const renderUpdateUserLevel = () => {
    if (!username || username === 'all' || !isAdmin) {
      return null;
    }

    return (
      <RectangularButton
        className="my-2"
        onClick={() => setShowUpdateUserLevelModal(true)}
        darkButton
      >
        Update user level
      </RectangularButton>
    );
  };

  const renderUpdateUserLevelModal = () => {
    if (!showUpdateUserLevelModal) {
      return null;
    }

    return (
      <UpdateUserLevelModal
        username={username}
        userLevel={userLevel}
        actions={actions}
        refetch={refetch}
        onClose={() => setShowUpdateUserLevelModal(false)}
      />
    );
  };

  return (
    <React.Fragment>
      {renderUpdateUserLevel()}
      <div className="user-level-graph">
        <Line
          ref={chartRef}
          height={100}
          data={chartData}
          options={options()}
          plugins={[ChartDataLabels, plugin]}
        />
      </div>
      {renderUpdateUserLevelModal()}
    </React.Fragment>
  );
}

function mapStateToProps(state, ownProps) {
  return {};
}

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(Actions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserLevelGraph);
